import styled from "styled-components";
import { MAX_VIDEO_SHOTS, withVideoMode } from "../../utils/videoShotPlan";

const Editor = styled.fieldset`
  border: 0; padding: 0; margin: 18px 0; min-width: 0;
  legend { font-weight: 700; margin-bottom: 10px; }
  label { display: block; font-size: 0.9rem; margin: 8px 0; }
  select, button { font: inherit; max-width: 100%; }
  select { margin: 4px 0; padding: 7px; border: 1px solid #cad5d4; border-radius: 6px; background: white; }
  p { font-size: 0.88rem; line-height: 1.5; color: #4b5563; }
  button { padding: 7px 10px; border: 1px solid #cad5d4; border-radius: 6px; background: white; color: #0b1c1e; }
  button:disabled { opacity: 0.55; cursor: not-allowed; }
`;
const Gallery = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px;
  button { text-align: left; display: flex; flex-direction: column; gap: 6px; overflow-wrap: anywhere; }
  button[aria-pressed="true"] { border: 2px solid #0e7e7a; background: #f0faf8; }
  img { width: 100%; aspect-ratio: 4 / 3; object-fit: cover; border-radius: 4px; }
  small { font-size: 0.75rem; color: #596565; }
`;
const ShotList = styled.ol`
  padding: 0; list-style: none;
  li { border: 1px solid #dce5e3; border-radius: 8px; padding: 10px; margin: 8px 0; overflow-wrap: anywhere; }
`;
const Actions = styled.div`display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px;`;

/** @param {{photos:import('../../utils/videoShotPlan').VideoPhoto[],
 * value:import('../../utils/videoShotPlan').VideoShotSelection,
 * onChange:(value:import('../../utils/videoShotPlan').VideoShotSelection)=>void,
 * disabled?:boolean, allowAutomatic?:boolean, aiEnabled?:boolean, aiPaused?:boolean}} props
 * aiPaused: the card already says AI motion is briefly paused, so the editor
 * does not repeat a vaguer "not available" line. */
const VideoShotEditor = ({
  photos,
  value,
  onChange,
  disabled = false,
  allowAutomatic = true,
  aiEnabled = true,
  aiPaused = false,
}) => {
  const manual = value.renderMode !== "ai";
  const selected = new Set(value.shotPlan.map((shot) => shot.photo));
  const missing = value.shotPlan.some(
    (shot) => !photos.some((photo) => photo.photo === shot.photo && photo.available !== false)
  );
  const labels = new Map(photos.map((photo) => [photo.photo, photo.label || photo.photo]));
  /** @param {Partial<import('../../utils/videoShotPlan').VideoShotSelection>} patch */
  const change = (patch) => {
    if (!disabled) onChange({ ...value, ...patch });
  };
  /** @param {number} index @param {number} delta */
  const move = (index, delta) => {
    const plan = [...value.shotPlan];
    [plan[index], plan[index + delta]] = [plan[index + delta], plan[index]];
    change({ shotPlan: plan });
  };
  return (
    <Editor disabled={disabled} aria-label="Video shot plan">
      <legend>Choose your video photos</legend>
      {allowAutomatic && (
        <label>
          Photo selection
          <select
            aria-label="Photo selection"
            value={manual ? "exact" : "automatic"}
            onChange={(event) =>
              // Leaving "Choose for me" keeps AI when the server offers it: the
              // exact-photos default is AI motion where suitable, not zooms only.
              change({
                ...withVideoMode(
                  value,
                  event.target.value === "automatic"
                    ? "ai"
                    : aiEnabled
                      ? "smart_mix"
                      : "photo_motion"
                ),
                modeChosen: true,
              })
            }
          >
            <option value="exact">Choose exact photos</option>
            <option value="automatic" disabled={!aiEnabled}>
              Choose for me
            </option>
          </select>
        </label>
      )}
      {manual ? (
        <>
          <label>
            Video style
            <select
              aria-label="Video style"
              value={value.renderMode}
              onChange={(event) =>
                change({
                  ...withVideoMode(
                    value,
                    event.target.value === "photo_motion" ? "photo_motion" : "smart_mix"
                  ),
                  modeChosen: true,
                })
              }
            >
              <option value="smart_mix" disabled={!aiEnabled}>
                AI motion where suitable (recommended)
              </option>
              <option value="photo_motion">Photo motion only — gentle zoom, no AI</option>
            </select>
          </label>
          <p>
            {value.renderMode === "photo_motion"
              ? "Gentle zooms of your original photos. No AI-generated property details."
              : "Each chosen photo is checked on its own: it gets AI camera motion when it passes our safety check, and a gentle zoom of the original when it doesn't (for example, a photo with people, pets or virtual staging). Up to 5 shots get AI motion. Photo motion is chosen before generation, not substituted after an uncertain AI request."}
          </p>
          {!aiEnabled && !aiPaused && (
            <p>AI motion is not available right now. You can still create a Photo motion video.</p>
          )}
          <label>
            Video format
            <select
              aria-label="Video format"
              value={value.aspectRatio}
              onChange={(event) =>
                change({ aspectRatio: event.target.value === "9:16" ? "9:16" : "16:9" })
              }
            >
              <option value="16:9">Landscape (16:9)</option>
              <option value="9:16">Vertical (9:16)</option>
            </select>
          </label>
          <p>
            Choose 1–6 photos. Your chosen photos appear in the order below. Same whole-video price,
            not a price per shot.
          </p>
          <p>
            Photo motion preserves the full view with borders where needed, followed by a gentle
            zoom. Preview the framing before sharing.
          </p>
          <Gallery aria-label="Available video photos">
            {photos.map((photo) => (
              <button
                key={photo.photo}
                type="button"
                aria-pressed={selected.has(photo.photo)}
                aria-label={`${selected.has(photo.photo) ? "Remove" : "Add"} ${photo.label || photo.photo}`}
                disabled={
                  disabled ||
                  photo.available === false ||
                  (!selected.has(photo.photo) && selected.size >= MAX_VIDEO_SHOTS)
                }
                onClick={() =>
                  change({
                    shotPlan: selected.has(photo.photo)
                      ? value.shotPlan.filter((shot) => shot.photo !== photo.photo)
                      : [
                          ...value.shotPlan,
                          {
                            photo: photo.photo,
                            motion: value.renderMode === "photo_motion" ? "photo_motion" : "auto",
                          },
                        ],
                  })
                }
              >
                <img src={photo.url} alt={photo.label || photo.photo} loading="lazy" />
                <span>{photo.label || photo.photo}</span>
                <small>
                  {photo.available === false
                    ? "Photo unavailable"
                    : photo.assessment === "ready"
                      ? "Previously assessed for AI motion"
                      : photo.assessment === "not_suitable"
                        ? "AI motion not recommended · Photo motion can be used"
                        : "AI suitability is checked when the video is made"}
                </small>
              </button>
            ))}
          </Gallery>
          {!photos.length && <p>Add photos before choosing your shots.</p>}
          <p aria-live="polite">
            {value.shotPlan.length} of {MAX_VIDEO_SHOTS} shots selected
          </p>
          {missing && (
            <p role="alert">
              A selected photo is no longer available. Remove it or refresh the source photos before
              continuing.
            </p>
          )}
          <ShotList aria-label="Selected shots in order">
            {value.shotPlan.map((shot, index) => (
              <li key={shot.photo}>
                <strong>
                  Shot {index + 1}: {labels.get(shot.photo) || shot.photo}
                </strong>
                <label>
                  Photo for shot {index + 1}
                  <select
                    aria-label={`Photo for shot ${index + 1}`}
                    value={shot.photo}
                    onChange={(event) =>
                      change({
                        shotPlan: value.shotPlan.map((entry, at) =>
                          at === index ? { ...entry, photo: event.target.value } : entry
                        ),
                      })
                    }
                  >
                    {!photos.some((photo) => photo.photo === shot.photo) && (
                      <option value={shot.photo}>Unavailable: {shot.photo}</option>
                    )}
                    {photos
                      .filter(
                        (photo) =>
                          photo.photo === shot.photo ||
                          (!selected.has(photo.photo) && photo.available !== false)
                      )
                      .map((photo) => (
                        <option key={photo.photo} value={photo.photo}>
                          {photo.label || photo.photo}
                        </option>
                      ))}
                  </select>
                </label>
                {value.renderMode === "smart_mix" && (
                  <label>
                    Motion for shot {index + 1}
                    <select
                      aria-label={`Motion for shot ${index + 1}`}
                      value={shot.motion}
                      onChange={(event) =>
                        change({
                          shotPlan: value.shotPlan.map((entry, at) =>
                            at === index
                              ? {
                                  ...entry,
                                  motion:
                                    event.target.value === "photo_motion" ? "photo_motion" : "auto",
                                }
                              : entry
                          ),
                        })
                      }
                    >
                      <option value="auto">Auto: AI motion or photo motion</option>
                      <option value="photo_motion">Photo motion only</option>
                    </select>
                  </label>
                )}
                <Actions>
                  <button
                    type="button"
                    aria-label={`Move shot ${index + 1} earlier`}
                    disabled={disabled || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    Move earlier
                  </button>
                  <button
                    type="button"
                    aria-label={`Move shot ${index + 1} later`}
                    disabled={disabled || index === value.shotPlan.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    Move later
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove shot ${index + 1}`}
                    onClick={() =>
                      change({
                        shotPlan: value.shotPlan.filter((entry) => entry.photo !== shot.photo),
                      })
                    }
                  >
                    Remove
                  </button>
                </Actions>
              </li>
            ))}
          </ShotList>
        </>
      ) : (
        <p>
          Listino chooses suitable photos for AI-generated clips. This option does not guarantee any
          particular photo or order.
        </p>
      )}
    </Editor>
  );
};

export default VideoShotEditor;
